import {
  isCheckoutRoute,
  useWelcomeCouponOffer,
} from "@/hooks/useWelcomeCouponOffer";
import { cn } from "@/libs/utils";
import { useIsBottomCtaBarVisible } from "@/store/bottom-cta-bar-store";
import { useWelcomeCouponSessionStore } from "@/store/welcome-coupon-popup-store";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { AuthUtils } from "@/utils/AuthUtils";
import { useTrans } from "@/utils/useTrans";
import { useRouter } from "next/router";

/**
 * 신규 가입 쿠폰 미니 배지 — `_app` 에 전역 마운트되는 떠 있는 알약.
 *
 * 팝업(`welcome-coupon-popup`)은 메인에만 뜨므로, 사용자가 팝업을 닫거나 메인을 벗어나면
 * 가입 유도 접점이 사라졌다. 이 배지가 그 자리를 대신한다 — **팝업이 안 보이는 동안에만**
 * 뜨고(둘이 동시에 뜨면 같은 혜택을 두 번 말하는 꼴), 클릭하면 바로 계정 만들기로 보낸다.
 *
 * 위치는 **좌하단**이다 — 우하단은 팝업(메인)이 쓰는 자리라 비워 둔다.
 * (dev 에서만 React Query Devtools 버튼이 같은 구석에 있는데, 프로덕션 빌드에는 없다.)
 *
 * 노출 조건은 팝업과 공유한다(`useWelcomeCouponOffer`). 여기서 추가로 보는 것은 세 가지다.
 * - **결제 화면 제외**: 결제 직전에 가입으로 이탈시키면 진행 중인 주문을 깬다.
 * - **모바일 구매 바 제외**: 그 바가 떠 있으면 숨는다(아래 판정부 주석 참조).
 * - **이번 방문 닫기**: [×] 를 누르면 그 탭에서는 배지도 팝업도 다시 뜨지 않는다
 *   (sessionStorage). 두 표면이 같은 제안이라 닫기 의사도 함께 적용된다.
 */
export default function WelcomeCouponBadge() {
  const { t } = useTrans(["gnb"]);
  const router = useRouter();
  const { eligible, popupVisible, offer } = useWelcomeCouponOffer();
  const badgeDismissed = useWelcomeCouponSessionStore((s) => s.badgeDismissed);
  const dismissBadge = useWelcomeCouponSessionStore((s) => s.dismissBadge);
  const bottomCtaBarVisible = useIsBottomCtaBarVisible();

  // `eligible` 이 참이면 `offer` 는 null 이 아니지만(같은 훅이 판정한다) 타입 좁히기를 위해
  // 둘 다 본다.
  if (!eligible || !offer || popupVisible || badgeDismissed) return null;
  if (isCheckoutRoute(router.pathname)) return null;
  // 모바일 제품 페이지의 하단 고정 구매 바가 떠 있으면 배지를 띄우지 않는다. 이유가 둘이다.
  // (1) 바 높이가 사용처마다 92~180px 로 달라 "몇 px 위로" 를 상수로 맞출 수 없다 — 초안의
  //     112px 는 구매 PDP 에서 `Add to Bag` 을 덮었다(실측: 배지 548~588 vs 버튼 568~608).
  // (2) 그 바 안에 이미 같은 캠페인의 가입 유도 배지가 있다(#5102 `Sign up & save {{percent}}%`).
  //     나란히 띄우면 같은 혜택을 두 줄로 두 번 말하게 된다.
  // 바는 푸터에 닿으면 사라지므로 그 구간에서는 배지가 다시 뜬다.
  if (bottomCtaBarVisible) return null;

  return (
    <div
      data-testid="welcome-coupon-badge"
      className={cn(
        // 동의 배너(z-100)·카트 시트보다 아래, 본문보다는 위. 배너가 떠 있는 동안은
        // `eligible` 이 false 라 애초에 그리지 않는다.
        "fixed z-50 flex items-stretch",
        "left-4 laptop:left-6",
        "bottom-4 laptop:bottom-6",
        "overflow-hidden rounded-full bg-bhaptics-yellow shadow-2xl",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2",
      )}
    >
      {/* 팝업의 CTA(#5146)와 같은 목적지 — auth 계정 만들기 화면, 성공 시 현재 페이지 복귀.
          가입 유도 문구를 달고 로그인으로 보내면 신규 사용자가 막다른 화면을 만난다. */}
      <a
        href={AuthUtils.getSignUpUrl()}
        className="py-3 pl-5 pr-1 text-sm font-700 text-coolgray-100 transition-colors hover:bg-black/5"
      >
        {t("Sign Up for {{percent}}% Off", { percent: offer.percent })}
      </a>
      {/* 링크 안에 버튼을 중첩하면 안 되므로(대화형 요소 중첩) 형제로 둔다. */}
      <button
        type="button"
        onClick={dismissBadge}
        className="flex items-center pl-1 pr-4 text-coolgray-90 transition-colors hover:bg-black/5"
      >
        <CrossIcon className="h-4 w-4" />
        <span className="sr-only">{t("Close")}</span>
      </button>
    </div>
  );
}
